import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { EPaper } from "scmp-app/components/posties/brochure-homepage/e-paper";
import { HearFromOurReaders } from "scmp-app/components/posties/brochure-homepage/hear-from-our-readers";
import { HeroArticles } from "scmp-app/components/posties/brochure-homepage/hero-articles";
import { Logo } from "scmp-app/components/posties/brochure-homepage/logo";
import { PostiesNewsFromScmpEducation } from "scmp-app/components/posties/brochure-homepage/news-from-scmp-education";
import { PostiesOurFeatures } from "scmp-app/components/posties/brochure-homepage/our-features";
import { SubscriptionPlan } from "scmp-app/components/posties/brochure-homepage/subscription-plan";
import { Testimonial } from "scmp-app/components/posties/brochure-homepage/testimonial";
import { WeHaveSoMuchForYou } from "scmp-app/components/posties/brochure-homepage/we-have-so-much-for-you";
import { WhyPosties } from "scmp-app/components/posties/brochure-homepage/why-posties";
import { postiesMenuData } from "scmp-app/components/posties/header/posties-brochure-header/header-menu/data";
import { PostiesSeo } from "scmp-app/components/posties/posties-seo";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import { usePostiesParentsRedirect } from "scmp-app/pages/posties/parents/hooks";
import type { parentsPostiesPageQuery } from "scmp-app/queries/__generated__/parentsPostiesPageQuery.graphql";

import { PageContainer, StyledAppFooter, StyledSignUpNewsletter } from "./styles";

const query = graphql`
  query parentsPostiesPageQuery {
    ...newsFromScmpEducationQuery
  }
`;

const PostiesPage: RelayComponent<unknown, parentsPostiesPageQuery> = ({ preloadedQuery }) => {
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    trackPageView?.();
  }, [trackPageView]);

  usePostiesParentsRedirect();
  const data = usePreloadedQuery(query, preloadedQuery);

  return (
    <>
      <PostiesSeo />
      <PageContainer>
        <Logo />
        <HeroArticles />
        <WhyPosties id={postiesMenuData[0].hash.slice(1)} />
        <HearFromOurReaders />
        <PostiesOurFeatures />
        <SubscriptionPlan id={postiesMenuData[2].hash.slice(1)} />
        <WeHaveSoMuchForYou />
        <EPaper id="epaper" />
        <PostiesNewsFromScmpEducation id={postiesMenuData[1].hash.slice(1)} reference={data} />
        <Testimonial />
        <StyledSignUpNewsletter />
        <StyledAppFooter variant="posties" />
      </PageContainer>
    </>
  );
};

PostiesPage.displayName = "Posties";

const pageProps: PageProps = {
  appBarConfiguration: {
    variant: "posties/brochure",
  },
  appInitializerConfiguration: {
    application: "posties",
  },
};

export default withRelay(PostiesPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
});
